import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Col, Row, Dropdown } from 'react-bootstrap';
import { routeName } from 'routes/routes-name';
import { useHistory } from 'react-router-dom';
import { Box, Grid, MenuItem } from '@mui/material';
import _ from 'lodash';

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/**API */
import { getBranch } from 'utils/app.utils';
import ClinicApi, { BranchInterface } from 'api/master/clinic.api';
import ReportApi, { FindAllReportInterface } from 'api/report.api';

/**COMPONENT */
import Loading from 'component/Loading';
import PatientList from './PatientList';
import HeaderCard from 'component/CardCustom/HeaderCard';

/**STYLE */
import PatientTypeApi from 'api/setting/default-users/patient-type.api';
import TextInput from 'new-components/inputs/text-input';
import SelectInput from 'new-components/inputs/select-input';
import SelectCheckBoxInput from 'new-components/inputs/select_checkbox_input';
import MainButton from 'new-components/buttons/main-button';
import DropdownButton from 'new-components/buttons/dropdown-button';
import exportPatientListToExcel from './ExportToExcel';
import { UseSnCode } from 'utils/useSnCode';

export default function PatientInformation() {
    const { t } = useTranslation();
    const history = useHistory();
    const componentRef = useRef<HTMLDivElement>(null);
    const [branchId, setBranchId] = useState<any>(getBranch());
    const [branches, setBranches] = useState<any[]>([]);
    const [rowCount, setRowCount] = useState<number>(0);
    const [sortBy, setSortBy] = useState<string>('createAt');
    const [sortType, setSortType] = useState<string>('ASC');
    const [search, setSearch] = useState<string>('');
    const [treatmentRightData, getTreatmentRightData] = useState<any[]>([]);
    const [treatmentRightId, setTreatmentRightId] = useState<number>(0);
    const [patientData, setPatientData] = useState<any[]>([]);
    const [patientDataExport, setPatientDataExport] = useState<any[]>([]);
    const [patientPage, setPatientPage] = useState<number>(1);
    const [patientLimitPage, setPatientLimitPage] = useState<number>(10);
    const [loading, setLoading] = useState<boolean>(true);
    const [patientTypeOption, setPatientTypeOption] = useState<any>([])
    const [patientTypes, setPatientTypes] = useState<any>([])
    const [resultPatientTypes, setResultPatientTypes] = useState('')

    const verifyPermission = async () => {
        const condition: FindAllReportInterface = {
            page: 1, pageLimit: 200
        }
        ReportApi.reportList(condition)
            .then(({ data }) => {
                const check = _.find(data, (val: any) => String(val.reportKey) === 'PATIENT_INFORMATION') ? true : false
                if (!check) {
                    history.push(routeName.report)
                    return false
                }
            })

        ClinicApi.findAllBranches()
            .then(({ data }) => setBranches(data))
            .catch((e) => { return })

        ClinicApi.findAllTreatmentRight()
            .then((resp) => { getTreatmentRightData(resp.data) })
            .catch((e) => { return })

        return true
    }

    const loadPatientType = async () => {
        const condition: any = {
            page: 1,
            pageLimit: 30,
            status: "ACTIVE"
        }

        const res = await PatientTypeApi.findAll(condition)
        if (res.status === 200) {
            setPatientTypeOption(_.map(res.data, (item) => ({ name: item.patientTypeName, id: item.patientTypeId })))
        }
    }

    useEffect(() => {
        verifyPermission()
        loadPatientType()
    }, [branchId])

    const fetchPatientInformation = async () => {
        await verifyPermission()
        await ReportApi.reportPatientInformation({
            page: patientPage,
            pageLimit: patientLimitPage,
            branchId: branchId,
            patientTypeId: resultPatientTypes,
            search: search,
            sortBy: sortBy,
            sortType: sortType,
            treatmentRightId: treatmentRightId,
        }).then(({ headers, data }) => {
            setPatientData(data.rows)
            setRowCount(headers['x-total'])
        }).catch((e) => setLoading(false))
            .finally(() => setLoading(false))
    }

    const fetchPatientInformationExport = async () => {
        await verifyPermission()
        await ReportApi.reportPatientInformation({
            page: patientPage,
            pageLimit: 10000,
            branchId: branchId,
            patientTypeId: resultPatientTypes,
            search: search,
            sortBy: sortBy,
            sortType: sortType,
            treatmentRightId: treatmentRightId,
            export: 1
        }).then(({ headers, data }) => {
            setPatientDataExport(data.rows)
            setRowCount(headers['x-total'])
        }).catch((e) => setLoading(false))
            .finally(() => setLoading(false))
    }

    const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
        setSortType(sortTypeVal)
        setSortBy(sortByVal)
    }

    const handleChangeRowsPerPage = (limit: number) => setPatientLimitPage(limit)

    const handleChangePage = (val: number) => setPatientPage(val)

    useEffect(() => {
        fetchPatientInformation()
        fetchPatientInformationExport()
    }, [patientPage, patientLimitPage])


    const checkNumber = (value: any): boolean => {
        return /^\d+$/.test(value);
    };

    const checkNull = (value: any): any => {
        return value || '-';
    };

    const toBuddhistYear = (momentObj: moment.Moment, format: string): string => {
        return momentObj.format(format);
    };

    const handleExportExcel = () => {
        exportPatientListToExcel({
            data: patientDataExport,
            filter: {
                branch: `${_.get(_.find(branches, { branchId: branchId }), 'branchName' || 'branchNameEn')}`
            },
            t: t,
            checkNumber: checkNumber,
            checkNull: checkNull,
            toBuddhistYear: toBuddhistYear,

        });
    };

    return (
        <Box>
            <Loading show={loading} type='fullLoading' />
            <HeaderCard text={t(`REPORT.REPORT_NAME.PATIENT_INFORMATION`)} />
            <Box marginX="24px" marginY="16px">
                <Grid container spacing="16px">
                    <Grid item sm={6} md={4} xl={3} >
                        <TextInput
                            label={t('REPORT.REPORT_NAME.SEARCH_PATIENT_INFORMATION')}
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value)
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item sm={6} md={4} xl={3} >
                        <SelectInput
                            onChange={(e) => {
                                setBranchId(e.target.value)
                            }}
                            value={branchId}
                            options={
                                _.map(branches, (item) => ({ value: item.branchId, name: item.branchName }))
                            }
                        />
                    </Grid>
                    <Grid item sm={6} md={4} xl={3} >
                        <SelectCheckBoxInput
                            fullWidth
                            onChange={(e) => {
                                setPatientTypes(e)
                                const result = e.join(',')
                                setResultPatientTypes(result)
                            }}
                            options={[{ name: t('NOT_SPECIFIED'), id: 0 }, ...patientTypeOption]}
                            selected={patientTypes}
                            label='ประเภทผู้ป่วย'
                        />
                    </Grid>
                    <Grid item xs={6} md={12} xl={3} >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <MainButton title='ค้นหา' type='primary' style={{ width: '120px' }} variant='contained' onClick={() => {
                                setPatientPage(1)
                                fetchPatientInformation()
                                fetchPatientInformationExport()
                            }} />
                            <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                                if (e === 'Excel') handleExportExcel()
                            }} options={[{ value: 'Excel', name: ' Excel' }]} />
                        </Box>
                    </Grid>
                </Grid>
                <Box marginTop="24px">
                    <PatientList
                        filter={{
                            branch: `${_.get(_.find(branches, { branchId: branchId }), 'branchName' || 'branchNameEn')}`
                        }}
                        page={patientPage}
                        pageLimit={patientLimitPage}
                        rowCount={rowCount}
                        sortBy={sortBy}
                        sortType={sortType}
                        data={patientData}
                        onRequestSort={onRequestSort}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        handleChangePage={handleChangePage}
                        componentRef={componentRef}
                        exportData={patientDataExport} />
                </Box>
            </Box>
        </Box>
    )
}